import { Injectable, inject, DOCUMENT } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  GTMAccountUpdateSteps,
  GTMEvents, GTMLoginEvents, GTMOnboardingEvents, GTMPasswordResetEvents, GTMPaymentMethod, GTMPlanAddOnEvents, GTMServiceRequestSteps, GTMTooltipInteractionType, LoginSteps, OnboardingSteps
} from 'src/app/model/google-tag-manager/api-status';
import { ISelectedContract, ISelectedPlan } from './settings';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { ADPResourceService } from 'digital-core';
import { Router, ActivatedRoute } from '@angular/router';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';

import { StorageService } from './storage.service';
import { UserSessionTrackingService } from './user-session-tracking.service';

// tslint:disable-next-line:ban-types
declare let gtag: Function; // Declare ga as a function

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  public selectedContract: ISelectedPlan;
  public oktaId: string;
  public contractId: string; // Only for onboarding scenario
  public eConsentOnboarding: {
    smsConsent?: false;
  } = {};
  public isPrimary: boolean = undefined; // Only for onboarding scenario
  public registrationMethod: string; // Only for onboarding scenario
  public loginMethod: string; // Only for account-lookup scenario
  private resourceService = inject(ADPResourceService);
  private selectedDeviceService = inject(SelectedDeviceService);
  private storageService = inject(StorageService);
  private userSessionTracking = inject(UserSessionTrackingService);
  private document = inject(DOCUMENT) as Document;

  public selectedAddOns: string;
  public selectedAddOnsCount: number;

  public emitClickEvent(eventCategory: string, elementName: string, linkURL?: string, linkClasses?: string, claimType?: string, title?: string) {
    const currentUrl = this.router.url;
    const pageTitle = title ?? this.getActiveRouteTitle();
    const location = this.document.location.href;
    const client = this.resourceService.getPortalSetting('Global_ProgramName').Value;

    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (eventCategory) {
      case GTMEvents.Click:
        this.emitGTMEvent({
          event: GTMEvents.Click,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            claim_type: claimType ?? '',
            link_text: elementName,
            link_classes: linkClasses ?? '',
            link_url: linkURL ?? '',
            timeStamp: dateTime,
          },
        });
        break;
      case GTMEvents.AccordionExpand:
        this.emitGTMEvent({
          event: GTMEvents.AccordionExpand,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            link_text: elementName, // Article - topic
            link_classes: linkClasses ?? '', // categories
            timeStamp: dateTime,
          },
        });
        break;
      case GTMEvents.ChatOpen:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMEvents.ChatOpen,
          event_data: {
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            link_text: elementName,
            timeStamp: dateTime,
            user_data: {
              okta_id: this.oktaId ?? '',
              contract_number: this.selectedContract?.planNumber ?? '',
              user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
            },
          },
        });
        break;
      case GTMEvents.ModalView:
        this.emitGTMEvent({
          event: GTMEvents.ModalView,
          event_data: {
            modal_category: linkClasses,
            modal_message: elementName,
            page_location: location,
            page_title: pageTitle,
            timeStamp: dateTime,
          },
        });
        break;
    }
  }

  public emitTooltipEvent(applianceType: string, interactionType: GTMTooltipInteractionType, screenContext: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    this.emitGTMEvent({
      event: GTMEvents.TooltipInteraction,
      event_data: {
        page_location: location,
        page_path: currentUrl,
        page_title: pageTitle,
        appliance_type: applianceType,
        interaction_type: interactionType,
        screen_context: screenContext,
        timeStamp: dateTime,
      },
      user_data: {
        okta_id: this.oktaId ?? '',
        contract_number: this.selectedContract?.planNumber ?? '',
        user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
      },
    });
  }

  public emitErrorEvent(message: string, type: string, code: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;

    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    this.emitGTMEvent({
      event: GTMEvents.Error,
      event_data: {
        page_location: location,
        page_path: currentUrl,
        page_title: pageTitle,
        error_message: message,
        error_type: type,
        error_code: code,
        timeStamp: dateTime,
        user_data: {
          okta_id: this.oktaId ?? '',
          contract_number: this.selectedContract?.planNumber ?? '',
          user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
        },
      },
    });
  }

  public emitGTMEvent(data: any) {
    const win = this.document.defaultView as Window & { dataLayer?: unknown[] };
    if (!win) {
      return;
    }
    win.dataLayer = win.dataLayer ?? [];
    const enriched = { ...data, user_session_id: this.userSessionTracking.getSessionId() };
    if (this.oktaId) {
      enriched.user_id = this.oktaId;
    }
    win.dataLayer.push(enriched);
  }

  public getErrorCodeForGTM(error: any) {
    if (error instanceof HttpErrorResponse) {
      let errorResponse: any;
      if (error.error && error.error.error) {
        errorResponse = error.error.error;
      } else {
        errorResponse = error;
      }
      if (errorResponse && errorResponse.hasOwnProperty('httpStatusCode')) {
        return errorResponse.code;
      } else {
        return `${error.status}: ${error.statusText}`;
      }
    }
    return 'ApplicationError';
  }

  public performanceEmitter(eventCategory: string, eventName: string, startTime: number) {
    const totalTime = Date.now() - startTime;

    gtag('event', 'timing_complete', {
      name: eventName,
      value: totalTime,
      event_category: eventCategory,
    });
  }

  public apiPerformanceEmitter(eventName: string, startTime: number) {
    const totalTime = Date.now() - startTime;
    this.emitGTMEvent({
      event: 'timing_complete',
      name: eventName,
      value: totalTime,
      event_category: 'api_timing',
      event_label: 'api',
    });
  }

  public isUserPrimary(contract: ISelectedContract, userClaims: UserClaims) {
    let check = false;
    const isEprismOnly = this.storageService.getLocal<string>(`IsEprismOnly`);
    if (isEprismOnly == 'true') {
      return true; // Eprism contract only is primary user
    }
    const loggedInMember = contract?.members?.find((m) => m?.emailAddress?.toLowerCase() === userClaims?.email?.toLowerCase());

    if (loggedInMember) {
      check = loggedInMember?.emailAddress?.toLowerCase() === contract?.primaryUserEmail?.toLowerCase();
    }

    return check;
  }

  public hashCode(s: string) {
    let hash = 0;
    if (s.length === 0) return hash;

    for (let i = 0; i < s.length; i++) {
      const charCode = s.charCodeAt(i);
      hash = (hash << 7) - hash + charCode;
      hash = hash & hash;
    }
    return hash;
  }

  public emitPageViewEvent() {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const devicesCount = this.selectedDeviceService.getAllDevices()?.length ?? 0;
    const client = this.resourceService.getPortalSetting('Global_ProgramName').Value;

    this.emitResetUserData();
    this.emitGTMEvent({
      event: GTMEvents.PageView,
      page_data: {
        page_location: location,
        page_path: currentUrl,
        client: client.toString(),
        device_count: devicesCount,
        page_title: pageTitle,
        plan_type: this.selectedContract?.planType ?? '',
      },
      user_data: {
        contract_number: this.selectedContract?.planNumber ?? '',
        user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
      },
    });
  }

  public emitDeviceInventoryEvent(event: string, flowType: string, deviceType: string, deviceMake: string, deviceModel: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const devicesCount = this.selectedDeviceService.getAllDevices()?.length ?? 0;
    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    switch (event) {
      case GTMEvents.DeviceInventoryView:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryView,
          event_data: {
            device_count: devicesCount,
            timeStamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddView:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddView,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            timeStamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddFail:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddFail,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            total_devices: devicesCount,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            timeStamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMEvents.DeviceInventoryAddComplete:
        this.emitGTMEvent({
          event: GTMEvents.DeviceInventoryAddComplete,
          event_data: {
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            flow_type: flowType,
            total_devices: devicesCount,
            device_type: deviceType,
            device_make: deviceMake,
            device_model: deviceModel,
            timeStamp: dateTime,
          },
          user_data: {
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
    }
  }

  public emitOnboardingEvent(event: GTMOnboardingEvents, step?: OnboardingSteps, error: any = undefined) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (event) {
      case GTMOnboardingEvents.Lookup:
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Lookup,
          event_data: {
            step_name: step.toString(),
            timeStamp: dateTime,
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
          },
        });
        break;
      case GTMOnboardingEvents.Start:
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Start,
          event_data: {
            step_name: step.toString(),
            timeStamp: dateTime,
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
          },
        });
        break;
      case GTMOnboardingEvents.InProcess:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMOnboardingEvents.InProcess,
          event_data: {
            step_name: step.toString(),
            eConsent_sms: this.formatConsentValue(this.eConsentOnboarding?.smsConsent),
            timeStamp: dateTime,
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            registration_method: this.registrationMethod ?? '',
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.contractId ?? '',
            user_type: this.isPrimary === undefined ? '' : this.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMOnboardingEvents.Complete:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Complete,
          event_data: {
            step_name: step.toString(),
            eConsent_sms: this.formatConsentValue(this.eConsentOnboarding?.smsConsent),
            timeStamp: dateTime,
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            registration_method: this.registrationMethod ?? '',
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.contractId ?? '',
            user_type: this.isPrimary === undefined ? '' : this.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMOnboardingEvents.Failed:
        this.emitGTMEvent({
          event: GTMOnboardingEvents.Failed,
          event_data: {
            step_name: step.toString(),
            timeStamp: dateTime,
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            error_message: error?.message,
            error_type: error?.type,
            error_code: error?.code,
          },
        });
        break;
      case GTMOnboardingEvents.FormComplete:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMOnboardingEvents.FormComplete,
          event_data: {
            timeStamp: dateTime,
            eConsent_sms: this.formatConsentValue(this.eConsentOnboarding?.smsConsent),
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            registration_method: this.registrationMethod ?? '',
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.contractId ?? '',
            user_type: this.isPrimary === undefined ? '' : this.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      default:
        break;
    }
  }

  public emitLoginEvent(event: GTMLoginEvents, error: any = undefined, pageReferrer?: string, step?: LoginSteps) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (event) {
      case GTMLoginEvents.Login:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMLoginEvents.Login,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            timeStamp: dateTime,
          },
        });
        break;
      case GTMLoginEvents.LoginSuccess:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginSuccess,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            page_referrer: pageReferrer ?? '',
            timeStamp: dateTime,
            login_method: this.loginMethod,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMLoginEvents.LoginError:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginError,
          event_data: {
            step_name: step?.toString() ?? '',
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            timeStamp: dateTime,
            error_message: error?.message,
            error_type: error?.type,
            error_code: error?.code,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMLoginEvents.LoginMFA:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginMFA,
          event_data: {
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            timeStamp: dateTime,
            mfa_method: 'email', // we are using only email for now
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMLoginEvents.LoginInProcess:
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginInProcess,
          event_data: {
            step_name: step?.toString() ?? '',
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            timeStamp: dateTime,
            login_method: this.loginMethod,
          },
        });
        break;
      case GTMLoginEvents.LoginComplete:
        this.emitGTMEvent({
          event: GTMLoginEvents.LoginComplete,
          event_data: {
            step_name: step?.toString() ?? '',
            page_title: pageTitle,
            page_path: currentUrl,
            page_location: location,
            timeStamp: dateTime,
            login_method: this.loginMethod,
          },
        });
        break;
      default:
        break;
    }
  }

  public getActiveRouteTitle(): string {
    let title = '';
    let route = this.activatedRoute;

    while (route) {
      if (route.snapshot.data && route.snapshot.data.title) {
        title = route.snapshot.data.title;
      }

      if (route.firstChild) {
        route = route.firstChild;
      } else {
        break;
      }
    }

    return title;
  }

  public setUserData(contract: ISelectedPlan, oktaId: string) {
    this.selectedContract = contract;
    this.oktaId = oktaId;
  }

  emitResetUserData() {
    this.emitGTMEvent({
      user_data: null,
    });
  }

  emitResetEventData() {
    this.emitGTMEvent({
      event_data: null,
    });
  }

  emitServiceRequestViewEvent(serviceRequestCount: number, serviceRequestStatus: string, claimNumber: string, claimType: string, childContract: string) {
    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetUserData();
    this.emitGTMEvent({
      event: GTMEvents.ServiceRequestView,
      page_data: {
        plan_type: this.selectedContract?.planType ?? '',
        serviceRequest_count: serviceRequestCount,
        serviceRequest_Status: serviceRequestStatus,
        claim_number: claimNumber,
        claim_type: claimType,
        timeStamp: dateTime,
      },
      user_data: {
        okta_id: this.oktaId ?? '',
        contract_number: this.selectedContract?.planNumber ?? '',
        user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
        child_contract_number: childContract ?? '',
      },
    });
  }

  emitServiceClaim(
    eventCategory: GTMEvents,
    step: GTMServiceRequestSteps,
    claimType: string,
    nickName: string,
    registrationItem: string,
    externalLink?: string,
    childContract?: string,
    errorMessage?: string,
    errorType?: string,
    errorCode?: string
  ) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    this.emitResetUserData();
    switch (eventCategory) {
      case GTMEvents.ServiceClaimStepView:
        this.emitGTMEvent({
          event: GTMEvents.ServiceClaimStepView,
          page_data: {
            step_name: step,
            claim_type: claimType,
            plan_type: this.selectedContract?.planType ?? '',
            item_nickname: nickName,
            registration_item: registrationItem,
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
            child_contract_number: childContract ?? '',
          },
        });
        break;
      case GTMEvents.ServiceClaimStepComplete:
        this.emitGTMEvent({
          event: GTMEvents.ServiceClaimStepComplete,
          page_data: {
            step_name: step,
            claim_type: claimType,
            item_nickname: nickName,
            registration_item: registrationItem,
            plan_type: this.selectedContract?.planType ?? '',
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            external_link: externalLink ?? '',
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
            child_contract_number: childContract ?? '',
          },
        });
        break;
      case GTMEvents.ServiceClaimFail:
        this.emitGTMEvent({
          event: GTMEvents.ServiceClaimFail,
          page_data: {
            step_name: step,
            claim_type: claimType,
            plan_type: this.selectedContract?.planType ?? '',
            item_nickname: nickName,
            page_location: location,
            page_path: currentUrl,
            page_title: pageTitle,
            error_message: errorMessage,
            error_type: errorType,
            error_code: errorCode,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
            child_contract_number: childContract ?? '',
          },
        });
        break;
    }
  }

  emitPlanAddOnEvent(eventCategory: GTMPlanAddOnEvents, addOnTotal?: string, serviceFee?: string) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    switch (eventCategory) {
      case GTMPlanAddOnEvents.PlanAddOnView:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMPlanAddOnEvents.PlanAddOnView,
          page_data: {
            plan_type: this.selectedContract?.planType ?? '',
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMPlanAddOnEvents.PlanAddOnFail:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMPlanAddOnEvents.PlanAddOnFail,
          page_data: {
            plan_type: this.selectedContract?.planType ?? '',
            addOn_count: this.selectedAddOnsCount,
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            error_message: 'Error',
            error_type: 'Error',
            error_code: 'Error',
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMPlanAddOnEvents.PlanAddOnStepComplete:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMPlanAddOnEvents.PlanAddOnStepComplete,
          page_data: {
            addOn_type: this.selectedAddOns,
            plan_type: this.selectedContract?.planType ?? '',
            addOn_count: this.selectedAddOnsCount,
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMPlanAddOnEvents.PlanAddOnPaymentView:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMPlanAddOnEvents.PlanAddOnPaymentView,
          page_data: {
            addOn_type: this.selectedAddOns,
            plan_type: this.selectedContract?.planType ?? '',
            addOn_count: this.selectedAddOnsCount,
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
      case GTMPlanAddOnEvents.PlanAddOnPaymentSuccess:
        this.emitResetUserData();
        this.emitGTMEvent({
          event: GTMPlanAddOnEvents.PlanAddOnPaymentSuccess,
          page_data: {
            addOn_type: this.selectedAddOns,
            plan_type: this.selectedContract?.planType ?? '',
            addOn_count: this.selectedAddOnsCount,
            addOn_total: addOnTotal,
            payment_type: 'Credit Card', // only payment type in this hw version
            service_fee: serviceFee,
            page_location: location,
            page_title: pageTitle,
            page_path: currentUrl,
            timeStamp: dateTime,
          },
          user_data: {
            okta_id: this.oktaId ?? '',
            contract_number: this.selectedContract?.planNumber ?? '',
            user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
          },
        });
        break;
    }
  }

  public emitPasswordResetEvent(event: GTMPasswordResetEvents, error: any = undefined) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetUserData();
    this.emitResetEventData();
    this.emitGTMEvent({
      event,
      event_data: {
        page_title: pageTitle,
        page_path: currentUrl,
        page_location: location,
        timeStamp: dateTime,
        ...(error && {
          error_message: error?.message,
          error_type: error?.type,
          error_code: error?.code,
        }),
      },
    });
  }

  emitAccountUpdateEvent(step: GTMAccountUpdateSteps) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();
    this.emitResetEventData();

    this.emitGTMEvent({
      event: GTMEvents.AccountUpdate,
      event_data: {
        plan_type: this.selectedContract?.planType ?? '',
        step_name: step,
        page_location: location,
        page_title: pageTitle,
        page_path: currentUrl,
        timeStamp: dateTime,
      },
      user_data: {
        okta_id: this.oktaId ?? '',
        contract_number: this.selectedContract?.planNumber ?? '',
        user_type: this.selectedContract?.isPrimary === undefined ? '' : this.selectedContract.isPrimary ? 'Primary' : 'Secondary',
      },
    });
  }

  public emitSavePaymentEvent(paymentMethod: GTMPaymentMethod) {
    const currentUrl = this.router.url;
    const pageTitle = this.getActiveRouteTitle();
    const location = this.document.location.href;
    const dateTime = new Date().toISOString();

    this.emitResetEventData();
    this.emitGTMEvent({
      event: GTMEvents.SavePayment,
      event_data: {
        timeStamp: dateTime,
        page_location: location,
        page_title: pageTitle,
        page_path: currentUrl,
        payment_method: paymentMethod,
      },
    });
  }

  private formatConsentValue(consent?: boolean): string {
    if (consent === undefined) {
      return '';
    }
    return consent ? 'Yes' : 'No';
  }
}
